import { useRef, useState } from "react";
import { X, Upload, ImageOff, Palette } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const MAX_LOGO_SIZE_MB = 2;
const INPUT_CLS =
  "bg-[#FFFFFF] border border-slate-200 rounded-none focus:ring-1 focus:ring-[#2563EB] focus:border-[#2563EB] mono text-sm h-10 px-3 w-full";

// Read a File → data URL (base64) safely.
function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export default function BrandSettingsDialog({ show, brand, onSave, onClose }) {
  const [draft, setDraft] = useState(brand);
  const fileInputRef = useRef(null);

  if (!show) return null;

  const handleLogoUpload = async (files) => {
    if (!files?.length) return;
    const f = files[0];
    if (!/^image\//.test(f.type)) {
      toast.error("Please upload an image file (PNG, JPG, WEBP, SVG)");
      return;
    }
    if (f.size > MAX_LOGO_SIZE_MB * 1024 * 1024) {
      toast.error(`Logo must be smaller than ${MAX_LOGO_SIZE_MB} MB`);
      return;
    }
    try {
      const dataUrl = await readFileAsDataUrl(f);
      setDraft((d) => ({ ...d, logo_data_url: dataUrl }));
    } catch {
      toast.error("Could not read logo file");
    }
  };

  const clearLogo = () => setDraft((d) => ({ ...d, logo_data_url: "" }));

  const handleSave = () => {
    onSave(draft);
    toast.success("Brand settings saved — future exports will use it");
  };

  const hasLogo = !!draft.logo_data_url;

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/25 backdrop-blur flex items-center justify-center p-4"
      data-testid="brand-settings-dialog"
      onClick={onClose}
    >
      <div
        className="bg-white border border-slate-200 w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl shadow-2xl shadow-slate-900/10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-[#FFFFFF]">
          <div className="flex items-center gap-2">
            <Palette className="w-5 h-5 text-[#2563EB]" />
            <div>
              <div className="overline">Export Header</div>
              <div className="heading text-lg font-semibold">Brand Settings</div>
            </div>
          </div>
          <button
            data-testid="close-brand-dialog"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-900 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <p className="text-xs text-[#64748B] leading-relaxed">
            When you provide your company details here, CargoLoad Pro's branding
            is <span className="text-[#2563EB] font-semibold">removed entirely</span>
            &nbsp;from the export header — your download goes out as your own
            document. Saved locally on this device — no upload, no account.
          </p>

          {/* Logo upload */}
          <div>
            <div className="overline mb-2">Company Logo (optional)</div>
            <div className="border-2 border-dashed border-white/15 hover:border-[#2563EB] transition-colors p-4 flex flex-col items-center justify-center gap-3 min-h-[140px] cursor-pointer"
                 onClick={() => fileInputRef.current?.click()}
                 data-testid="brand-logo-dropzone"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                className="hidden"
                data-testid="brand-logo-input"
                onChange={(e) => handleLogoUpload(e.target.files)}
              />
              {hasLogo ? (
                <>
                  <img
                    src={draft.logo_data_url}
                    alt="Custom logo preview"
                    className="max-h-20 max-w-[220px] object-contain bg-slate-100 p-2"
                    data-testid="brand-logo-preview"
                  />
                  <div className="mono text-[10px] text-[#64748B] uppercase tracking-widest">
                    Click to replace
                  </div>
                </>
              ) : (
                <>
                  <Upload className="w-8 h-8 text-[#2563EB]" />
                  <div className="mono text-xs text-slate-700">Click to upload logo</div>
                  <div className="mono text-[10px] text-[#64748B] uppercase tracking-widest">
                    PNG · JPG · WEBP · SVG · max {MAX_LOGO_SIZE_MB} MB
                  </div>
                </>
              )}
            </div>
            {hasLogo && (
              <button
                type="button"
                onClick={clearLogo}
                data-testid="brand-logo-clear"
                className="mt-2 mono text-[10px] uppercase tracking-widest text-red-400 hover:text-red-300 inline-flex items-center gap-1"
              >
                <ImageOff className="w-3 h-3" /> Remove logo
              </button>
            )}
          </div>

          <label className="flex flex-col gap-1">
            <span className="overline">Company Name</span>
            <Input
              data-testid="brand-company-input"
              className={INPUT_CLS}
              placeholder="e.g. Sabarmati Roadlines Pvt Ltd"
              value={draft.company_name || ""}
              onChange={(e) => setDraft((d) => ({ ...d, company_name: e.target.value }))}
              maxLength={80}
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="overline">Tagline (shown under company name)</span>
            <Input
              data-testid="brand-tagline-input"
              className={INPUT_CLS}
              placeholder="e.g. Trusted heavy transporter since 1997"
              value={draft.tagline || ""}
              onChange={(e) => setDraft((d) => ({ ...d, tagline: e.target.value }))}
              maxLength={80}
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="overline">Address (postal)</span>
            <textarea
              data-testid="brand-address-input"
              className={INPUT_CLS + " h-16 resize-none py-2"}
              placeholder="e.g. Plot 12, Sabarmati Industrial Area, Ahmedabad — 380005, Gujarat"
              value={draft.address || ""}
              onChange={(e) => setDraft((d) => ({ ...d, address: e.target.value }))}
              maxLength={200}
              rows={2}
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="overline">Contact (phone · email · GST)</span>
            <Input
              data-testid="brand-contact-input"
              className={INPUT_CLS}
              placeholder="e.g. +91 98765 43210 · info@sabarmati.co.in · GST 24ABCDE1234F1Z5"
              value={draft.contact || ""}
              onChange={(e) => setDraft((d) => ({ ...d, contact: e.target.value }))}
              maxLength={140}
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="overline">Website</span>
            <Input
              data-testid="brand-website-input"
              className={INPUT_CLS}
              placeholder="e.g. sabarmatiroadlines.co.in"
              value={draft.website || ""}
              onChange={(e) => setDraft((d) => ({ ...d, website: e.target.value }))}
              maxLength={60}
            />
          </label>

          <div className="mono text-[10px] text-slate-400 border border-slate-200 p-3 leading-relaxed">
            <span className="text-[#2563EB]">Tip:</span> Fill in the fields you
            want to show. Leave the rest blank — your logo without a tagline is
            fine, or just your company name without a logo also works.
          </div>
        </div>

        <div className="p-5 border-t border-slate-200 flex justify-end gap-2 bg-[#FFFFFF] sticky bottom-0">
          <Button
            variant="ghost"
            data-testid="brand-cancel-btn"
            onClick={onClose}
            className="rounded-md border border-slate-200 text-slate-600 hover:bg-slate-100"
          >
            Cancel
          </Button>
          <Button
            data-testid="brand-save-btn"
            onClick={handleSave}
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-md font-semibold mono uppercase tracking-widest text-xs"
          >
            Save Brand
          </Button>
        </div>
      </div>
    </div>
  );
}
